import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { Property, Owner, Activity, OwnerDocument } from '@/lib/types';
import { formatCurrency, ZONE_LABELS, LICENSE_STATUS_LABELS, PROPERTY_STATE_LABELS } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ActivityTimeline from '@/components/owners/ActivityTimeline';
import PropertyDocumentSection from '@/components/properties/PropertyDocumentSection';
import TarjetaDispositivosPropiedad from '@/components/dispositivos/TarjetaDispositivosPropiedad';
import {
  ArrowLeft, Trash2, Plus, MapPin, Bed, Bath, Maximize,
  Waves, Building, Image, User, ChevronLeft, ChevronRight, X,
  Pencil, Save, Loader2, Home, ToggleLeft, ToggleRight,
  FolderOpen, Clock
} from 'lucide-react';

type TabKey = 'info' | 'fotos' | 'distribucion' | 'documentos' | 'actividad';

const ROOM_ITEM_LABELS: Record<string, string> = {
  edredon: 'Edredón',
  fundaEdredon: 'Funda edredón',
  fundaColchon: 'Funda colchón',
  protectorColchon: 'Protector colchón',
  cubreSofa: 'Cubre sofá',
  fundaSofa: 'Funda sofá',
  cojinCuadrante: 'Cojín cuadrante',
  almohadaExtra: 'Almohada extra',
  manta: 'Manta',
  colcha: 'Colcha',
  protectorAlmohada: 'Protector almohada',
};

const BED_SIZE_LABEL: Record<number, string> = {
  90: 'Individual',
  105: 'Individual+',
  135: 'Matrimonio S',
  150: 'Matrimonio',
  160: 'Queen',
  180: 'King',
};

const ROOM_TYPE_STYLE: Record<string, { border: string; bg: string; text: string; dot: string }> = {
  'Dormitorio':         { border: 'border-indigo-300', bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-400' },
  'Salón-Dormitorio':  { border: 'border-purple-300', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-400' },
  'Salón':              { border: 'border-amber-300',  bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-400' },
  'Estudio':            { border: 'border-teal-300',   bg: 'bg-teal-50',   text: 'text-teal-700',   dot: 'bg-teal-400' },
  'Cocina-Comedor':     { border: 'border-green-300',  bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-400' },
};
const DEFAULT_ROOM_STYLE = { border: 'border-gray-200', bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' };

export default function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabKey) || 'info';
  const setActiveTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const [showAddActivity, setShowAddActivity] = useState(false);
  const [photoIndex, setPhotoIndex] = useState<number | null>(null);
  const [editSection, setEditSection] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [propertyDocs, setPropertyDocs] = useState<OwnerDocument[]>([]);

  const { data: property, loading, refresh } = useApi<Property>(() => api.getProperty(id!), [id]);
  const { data: hostifyPhotos, loading: loadingPhotos } = useApi<{ id: number; url: string; thumbnail?: string; caption?: string }[]>(
    () => api.getPropertyPhotos(id!), [id]
  );
  const { data: hostifyDetails } = useApi<{ bedrooms: number; bathrooms: number; beds: number; area: number; floor: number | null; personCapacity: number; hasPool: boolean; hasTerrace: boolean; hasParking: boolean; hasAC: boolean; hasWifi: boolean; elevator: boolean } | null>(
    () => api.getHostifyDetails(id!), [id]
  );

  const { data: roomDistribution, loading: loadingRooms } = useApi<{
    rooms: { roomId: string; name: string; roomType: string; personCapacity: number; beds: { tipo: string; tamano: number; almohadas: { cantidad: number; tamano: number } }[]; items: { tipo: string; tamano?: number; cantidad: number }[] }[];
    notes: string | null;
    fullSeason: boolean;
    activityStartDate: string | null;
    activityEndDate: string | null;
    updatedAt: string | null;
  } | null>(
    () => api.getRoomDistribution(id!), [id]
  );

  const { data: activities, refresh: refreshActivities } = useApi<Activity[]>(
    () => api.getActivities({ entityId: id! }),
    [id]
  );

  const { data: owner } = useApi<Owner | null>(
    () => property?.ownerId ? api.getOwner(property.ownerId) : Promise.resolve(null),
    [property?.ownerId]
  );

  useEffect(() => {
    if (id) api.getPropertyDocuments(id).then(setPropertyDocs).catch(() => setPropertyDocs([]));
  }, [id]);

  if (loading || !property) return <LoadingSpinner />;

  const refreshDocs = () => {
    api.getPropertyDocuments(id!).then(setPropertyDocs).catch(() => setPropertyDocs([]));
  };

  const handleUpdate = async (data: any) => {
    setSaving(true);
    try {
      await api.updateProperty(id!, data);
      setEditSection(null);
      refresh();
    } catch {
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (section: string, initialData: Record<string, any>) => {
    setEditForm(initialData);
    setEditSection(section);
  };

  const cancelEdit = () => {
    setEditSection(null);
    setEditForm({});
  };

  const saveSection = () => {
    const data: Record<string, any> = { ...editForm };
    if (data.features) {
      data.features = {
        ...data.features,
        distanceToBeachMeters: data.features.distanceToBeachMeters ? Number(data.features.distanceToBeachMeters) : undefined,
      };
    }
    if (data.estimatedAnnualRevenue) data.estimatedAnnualRevenue = Number(data.estimatedAnnualRevenue);
    if (data.estimatedOccupancyPercent) data.estimatedOccupancyPercent = Number(data.estimatedOccupancyPercent);
    handleUpdate(data);
  };

  const handleDelete = async () => {
    if (confirm('¿Eliminar esta propiedad?')) {
      await api.deleteProperty(id!);
      navigate('/properties');
    }
  };

  const handleAddActivity = async (data: any) => {
    await api.createActivity({
      ...data,
      entityId: id,
      entityType: 'property',
    });
    setShowAddActivity(false);
    refreshActivities();
  };

  const f = property.features || {};
  const bedrooms = hostifyDetails?.bedrooms ?? f.bedrooms ?? 0;
  const bathrooms = hostifyDetails?.bathrooms ?? f.bathrooms ?? 0;
  const sqMeters = hostifyDetails?.area ?? f.sqMeters ?? 0;
  const floor = hostifyDetails?.floor ?? f.floor;
  const hasPool = hostifyDetails?.hasPool ?? f.hasPool ?? false;
  const hasTerrace = hostifyDetails?.hasTerrace ?? f.hasTerrace ?? false;
  const hasParking = hostifyDetails?.hasParking ?? f.hasParking ?? false;
  const hasAC = hostifyDetails?.hasAC ?? f.hasAC ?? false;
  const hasWifi = hostifyDetails?.hasWifi ?? f.hasWifi ?? false;
  const elevator = hostifyDetails?.elevator ?? f.elevator ?? false;

  const docsCount = propertyDocs.length;

  return (
    <div>
      <button onClick={() => navigate('/properties')} className="btn-ghost mb-4 -ml-4">
        <ArrowLeft size={16} className="inline mr-1" /> Propiedades
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">{property.nickname || property.name}</h1>
          {property.nickname && property.name !== property.nickname && (
            <p className="text-sm text-sereno-dark/50 mt-0.5">{property.name}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className={`badge ${property.registrationType === 'VUT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
              {property.registrationType}
            </span>
            {property.propertyType && <span className="badge bg-gray-100 text-gray-600">{property.propertyType}</span>}
            {owner && (
              <button
                onClick={() => navigate(`/owners/${owner.id}`)}
                className="flex items-center gap-1.5 badge bg-sereno-dark/5 text-sereno-dark/60 hover:bg-sereno-blue/20 transition-colors cursor-pointer"
              >
                <User size={11} />
                {owner.firstName} {owner.lastName}
              </button>
            )}
          </div>
        </div>
        <button onClick={handleDelete} className="btn-danger flex items-center gap-1 flex-shrink-0">
          <Trash2 size={16} />
        </button>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 mb-6 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto">
        <nav className="flex gap-1 sm:gap-2 min-w-max">
          {([
            { key: 'info',        label: 'Información',  icon: Home },
            { key: 'fotos',       label: 'Fotos',        icon: Image,     count: hostifyPhotos?.length },
            { key: 'distribucion',label: 'Distribución', icon: Building },
            { key: 'documentos',  label: 'Documentos',   icon: FolderOpen, count: docsCount },
            { key: 'actividad',   label: 'Actividad',    icon: Clock,     count: activities?.length },
          ] as { key: TabKey; label: string; icon: any; count?: number }[]).map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-sereno-dark text-sereno-dark'
                    : 'border-transparent text-sereno-dark/40 hover:text-sereno-dark/70 hover:border-gray-300'
                }`}
              >
                <Icon size={15} />
                <span>{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-sereno-dark text-white' : 'bg-gray-100 text-sereno-dark/50'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* === TAB: INFORMACIÓN === */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Location */}
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Ubicación</h3>
              {editSection === 'location' ? (
                <div className="flex gap-1">
                  <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-100"><X size={14} /></button>
                  <button onClick={saveSection} disabled={saving} className="p-1 rounded hover:bg-gray-100 text-green-600">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              ) : (
                <button onClick={() => startEdit('location', {
                  address: { street: property.address?.street || '', zone: property.address?.zone || '', city: property.address?.city || '', province: property.address?.province || '', postalCode: property.address?.postalCode || '' },
                  cadastralReference: property.cadastralReference || '',
                  parkingSpace: property.parkingSpace || '',
                })} className="p-1 rounded hover:bg-gray-100 text-sereno-dark/40"><Pencil size={14} /></button>
              )}
            </div>
            {editSection === 'location' ? (
              <div className="space-y-2 text-sm">
                <div><label className="text-sereno-dark/50 text-xs">Calle</label><input className="input text-sm" value={editForm.address?.street || ''} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, street: e.target.value } }))} /></div>
                <div><label className="text-sereno-dark/50 text-xs">Zona</label>
                  <select className="input text-sm" value={editForm.address?.zone || ''} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, zone: e.target.value } }))}>
                    <option value="">—</option>
                    {Object.entries(ZONE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-sereno-dark/50 text-xs">Ciudad</label><input className="input text-sm" value={editForm.address?.city || ''} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, city: e.target.value } }))} /></div>
                  <div><label className="text-sereno-dark/50 text-xs">CP</label><input className="input text-sm" value={editForm.address?.postalCode || ''} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, postalCode: e.target.value } }))} /></div>
                </div>
                <div><label className="text-sereno-dark/50 text-xs">Ref. catastral</label><input className="input text-sm" value={editForm.cadastralReference || ''} onChange={e => setEditForm(f => ({ ...f, cadastralReference: e.target.value }))} /></div>
                <div><label className="text-sereno-dark/50 text-xs">Plaza parking</label><input className="input text-sm" value={editForm.parkingSpace || ''} onChange={e => setEditForm(f => ({ ...f, parkingSpace: e.target.value }))} /></div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin size={14} className="text-sereno-dark/40" />
                  <span>{property.address?.street || '—'}, {property.address?.city || '—'} {property.address?.postalCode || ''}</span>
                </div>
                <div className="text-sm"><span className="text-sereno-dark/50">Zona:</span> {ZONE_LABELS[property.address?.zone] || property.address?.zone || '—'}</div>
                {property.cadastralReference && <div className="text-sm"><span className="text-sereno-dark/50">Ref. catastral:</span> {property.cadastralReference}</div>}
                {property.parkingSpace && <div className="text-sm"><span className="text-sereno-dark/50">Plaza parking:</span> {property.parkingSpace}</div>}
              </>
            )}
          </div>

          {/* License */}
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Licencias</h3>
              {editSection === 'license' ? (
                <div className="flex gap-1">
                  <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-100"><X size={14} /></button>
                  <button onClick={() => {
                    const data: any = { registrationType: editForm.registrationType, touristLicense: { ...property.touristLicense, ...editForm.touristLicense }, ses: { ...property.ses, ...editForm.ses } };
                    handleUpdate(data);
                  }} disabled={saving} className="p-1 rounded hover:bg-gray-100 text-green-600">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              ) : (
                <button onClick={() => startEdit('license', {
                  registrationType: property.registrationType || 'VUT',
                  touristLicense: {
                    hasLicense: property.touristLicense?.hasLicense ?? false,
                    licenseNumber: property.touristLicense?.licenseNumber || '',
                    nraNumber: property.touristLicense?.nraNumber || '',
                    status: property.touristLicense?.status || 'none',
                  },
                  ses: {
                    codigoEstablecimiento: property.ses?.codigoEstablecimiento || '',
                  }
                })} className="p-1 rounded hover:bg-gray-100 text-sereno-dark/40"><Pencil size={14} /></button>
              )}
            </div>
            {editSection === 'license' ? (
              <div className="space-y-2 text-sm">
                <div><label className="text-sereno-dark/50 text-xs">Tipo de registro</label>
                  <select className="input text-sm" value={editForm.registrationType || 'VUT'} onChange={e => setEditForm(f => ({ ...f, registrationType: e.target.value }))}>
                    <option value="VUT">VUT (Vivienda Uso Turístico)</option>
                    <option value="AT">AT (Apartamento Turístico)</option>
                  </select>
                </div>
                <div><label className="text-sereno-dark/50 text-xs">Licencia VUT/AT</label><input className="input text-sm" value={editForm.touristLicense?.licenseNumber || ''} onChange={e => setEditForm(f => ({ ...f, touristLicense: { ...f.touristLicense, licenseNumber: e.target.value } }))} placeholder="Número de licencia" /></div>
                <div><label className="text-sereno-dark/50 text-xs">Código SES</label><input className="input text-sm font-mono" value={editForm.ses?.codigoEstablecimiento || ''} onChange={e => setEditForm(f => ({ ...f, ses: { ...f.ses, codigoEstablecimiento: e.target.value } }))} placeholder="Código establecimiento SES" /></div>
                {(editForm.registrationType || property.registrationType) !== 'AT' && (
                  <div><label className="text-sereno-dark/50 text-xs">NRA</label><input className="input text-sm font-mono" value={editForm.touristLicense?.nraNumber || ''} onChange={e => setEditForm(f => ({ ...f, touristLicense: { ...f.touristLicense, nraNumber: e.target.value } }))} placeholder="Número NRA" /></div>
                )}
                <div><label className="text-sereno-dark/50 text-xs">Estado</label>
                  <select className="input text-sm" value={editForm.touristLicense?.status || 'none'} onChange={e => setEditForm(f => ({ ...f, touristLicense: { ...f.touristLicense, status: e.target.value } }))}>
                    {Object.entries(LICENSE_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={editForm.touristLicense?.hasLicense ?? false} onChange={e => setEditForm(f => ({ ...f, touristLicense: { ...f.touristLicense, hasLicense: e.target.checked } }))} />
                  <label className="text-sm">Tiene licencia</label>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-sereno-dark/50">Tipo</span><span>{property.registrationType}</span></div>
                <div className="flex justify-between"><span className="text-sereno-dark/50">Licencia VUT/AT</span><span className="font-mono">{property.touristLicense?.licenseNumber || '—'}</span></div>
                <div className="flex justify-between gap-2"><span className="text-sereno-dark/50 flex-shrink-0">Código SES</span><span className="font-mono text-right break-all text-xs">{property.ses?.codigoEstablecimiento || '—'}</span></div>
                {property.registrationType !== 'AT' && (
                  <div className="flex justify-between gap-2"><span className="text-sereno-dark/50 flex-shrink-0">NRA</span><span className="font-mono text-right break-all text-xs">{property.touristLicense?.nraNumber || '—'}</span></div>
                )}
                <div className="flex justify-between"><span className="text-sereno-dark/50">Estado</span><span>{LICENSE_STATUS_LABELS[property.touristLicense?.status] || '—'}</span></div>
              </div>
            )}
          </div>

          {/* Features */}
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Características</h3>
              {editSection === 'features' ? (
                <div className="flex gap-1">
                  <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-100"><X size={14} /></button>
                  <button onClick={saveSection} disabled={saving} className="p-1 rounded hover:bg-gray-100 text-green-600">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              ) : (
                <button onClick={() => startEdit('features', {
                  features: { seaView: f.seaView || false, distanceToBeachMeters: f.distanceToBeachMeters || '' }
                })} className="p-1 rounded hover:bg-gray-100 text-sereno-dark/40"><Pencil size={14} /></button>
              )}
            </div>
            {editSection === 'features' ? (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-sereno-dark/50 text-xs">Dist. playa (m)</label><input type="number" className="input text-sm" value={editForm.features?.distanceToBeachMeters ?? ''} onChange={e => setEditForm(f => ({ ...f, features: { ...f.features, distanceToBeachMeters: e.target.value } }))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {[['seaView', 'Vistas mar']].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={editForm.features?.[key] ?? false} onChange={e => setEditForm(f => ({ ...f, features: { ...f.features, [key]: e.target.checked } }))} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1"><Bed size={14} className="text-sereno-dark/40" /> {bedrooms} hab.</div>
                  <div className="flex items-center gap-1"><Bath size={14} className="text-sereno-dark/40" /> {bathrooms} baños</div>
                  <div className="flex items-center gap-1"><Maximize size={14} className="text-sereno-dark/40" /> {sqMeters} m²</div>
                  {floor != null && <div className="flex items-center gap-1"><Building size={14} className="text-sereno-dark/40" /> Planta {floor}</div>}
                  {f.distanceToBeachMeters != null && <div className="flex items-center gap-1"><Waves size={14} className="text-sereno-dark/40" /> {f.distanceToBeachMeters}m playa</div>}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {hasPool && <span className="badge bg-blue-50 text-blue-600">Piscina</span>}
                  {hasTerrace && <span className="badge bg-green-50 text-green-600">Terraza</span>}
                  {hasParking && <span className="badge bg-gray-100 text-gray-600">Parking</span>}
                  {hasAC && <span className="badge bg-sky-50 text-sky-600">A/C</span>}
                  {hasWifi && <span className="badge bg-indigo-50 text-indigo-600">WiFi</span>}
                  {f.seaView && <span className="badge bg-cyan-50 text-cyan-600">Vistas mar</span>}
                  {elevator && <span className="badge bg-amber-50 text-amber-600">Ascensor</span>}
                  {!hasPool && !hasTerrace && !hasParking && !hasAC && !hasWifi && !f.seaView && !elevator && (
                    <span className="text-sereno-dark/30 text-sm">Sin extras</span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Revenue */}
          {/* Lo instalado en esta casa, con enlace a cada ficha del inventario */}
          <TarjetaDispositivosPropiedad propertyId={property.id} />

          <div className="card space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Rentabilidad estimada</h3>
              {editSection === 'revenue' ? (
                <div className="flex gap-1">
                  <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-100"><X size={14} /></button>
                  <button onClick={saveSection} disabled={saving} className="p-1 rounded hover:bg-gray-100 text-green-600">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              ) : (
                <button onClick={() => startEdit('revenue', {
                  estimatedAnnualRevenue: property.estimatedAnnualRevenue || '',
                  estimatedOccupancyPercent: property.estimatedOccupancyPercent || '',
                  currentState: property.currentState || 'empty',
                })} className="p-1 rounded hover:bg-gray-100 text-sereno-dark/40"><Pencil size={14} /></button>
              )}
            </div>
            {editSection === 'revenue' ? (
              <div className="space-y-2 text-sm">
                <div><label className="text-sereno-dark/50 text-xs">Ingresos anuales (€)</label><input type="number" className="input text-sm" value={editForm.estimatedAnnualRevenue ?? ''} onChange={e => setEditForm(f => ({ ...f, estimatedAnnualRevenue: e.target.value }))} /></div>
                <div><label className="text-sereno-dark/50 text-xs">Ocupación (%)</label><input type="number" min="0" max="100" className="input text-sm" value={editForm.estimatedOccupancyPercent ?? ''} onChange={e => setEditForm(f => ({ ...f, estimatedOccupancyPercent: e.target.value }))} /></div>
                <div><label className="text-sereno-dark/50 text-xs">Estado actual</label>
                  <select className="input text-sm" value={editForm.currentState || 'empty'} onChange={e => setEditForm(f => ({ ...f, currentState: e.target.value }))}>
                    {Object.entries(PROPERTY_STATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-sereno-dark/50">Ingresos anuales</span>
                  <span className="font-semibold">{property.estimatedAnnualRevenue ? formatCurrency(property.estimatedAnnualRevenue) : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sereno-dark/50">Ocupación</span>
                  <span>{property.estimatedOccupancyPercent ? `${property.estimatedOccupancyPercent}%` : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sereno-dark/50">Estado actual</span>
                  <span>{PROPERTY_STATE_LABELS[property.currentState] || '—'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Renting */}
          {(() => {
            const RENTING_MAX_GUESTS = 8; // tabla estándar de renting: 1–8 huéspedes
            const rentingEnabled = editSection === 'renting'
              ? (editForm.renting?.enabled ?? true)
              : (property.renting?.enabled ?? true);
            const costPerGuest: Record<string, number> = editSection === 'renting'
              ? (editForm.renting?.costPerGuest ?? {})
              : (property.renting?.costPerGuest ?? {});

            return (
              <div className="card space-y-3 lg:col-span-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Renting</h3>
                  {editSection === 'renting' ? (
                    <div className="flex gap-1">
                      <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-100"><X size={14} /></button>
                      <button onClick={saveSection} disabled={saving} className="p-1 rounded hover:bg-gray-100 text-green-600">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => startEdit('renting', { renting: { enabled: property.renting?.enabled ?? true, costPerGuest: property.renting?.costPerGuest ?? {} } })} className="p-1 rounded hover:bg-gray-100 text-sereno-dark/40"><Pencil size={14} /></button>
                  )}
                </div>
                {editSection === 'renting' ? (
                  <button type="button" onClick={() => setEditForm(f => ({ ...f, renting: { ...f.renting, enabled: !f.renting?.enabled } }))} className="flex items-center gap-2 text-sm">
                    {editForm.renting?.enabled ? <ToggleRight size={22} className="text-green-500" /> : <ToggleLeft size={22} className="text-sereno-dark/30" />}
                    <span className={editForm.renting?.enabled ? 'text-sereno-dark' : 'text-sereno-dark/40'}>{editForm.renting?.enabled ? 'Activo' : 'Inactivo'}</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-2 text-sm">
                    {rentingEnabled ? <ToggleRight size={20} className="text-green-500" /> : <ToggleLeft size={20} className="text-sereno-dark/30" />}
                    <span className={rentingEnabled ? 'text-sereno-dark' : 'text-sereno-dark/40'}>{rentingEnabled ? 'Activo' : 'Inactivo'}</span>
                  </div>
                )}
                {rentingEnabled && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 pt-1">
                    {Array.from({ length: RENTING_MAX_GUESTS }, (_, i) => i + 1).map(n => {
                      const key = String(n);
                      if (editSection === 'renting') {
                        return (
                          <div key={n} className="flex items-center gap-2">
                            <span className="text-xs text-sereno-dark/50 w-24 shrink-0">{n} {n === 1 ? 'huésped' : 'huéspedes'}</span>
                            <div className="relative">
                              <input type="number" min="0" step="0.5" className="input text-sm w-24 pr-6" placeholder="—"
                                value={editForm.renting?.costPerGuest?.[key] ?? ''}
                                onChange={e => setEditForm(f => ({ ...f, renting: { ...f.renting, costPerGuest: { ...f.renting?.costPerGuest, [key]: e.target.value === '' ? undefined : Number(e.target.value) } } }))}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-sereno-dark/40 pointer-events-none">€</span>
                            </div>
                          </div>
                        );
                      }
                      const cost = costPerGuest[key];
                      return (
                        <div key={n} className="flex items-center justify-between text-sm">
                          <span className="text-sereno-dark/50">{n} {n === 1 ? 'huésped' : 'huéspedes'}</span>
                          <span className={cost != null ? 'font-medium' : 'text-sereno-dark/25'}>{cost != null ? `${cost} €` : '—'}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!rentingEnabled && editSection !== 'renting' && (
                  <p className="text-xs text-sereno-dark/30">Renting desactivado para esta propiedad</p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* === TAB: FOTOS === */}
      {activeTab === 'fotos' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
              Fotos ({hostifyPhotos?.length || 0})
            </h3>
          </div>
          {loadingPhotos && property.hostifyId ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-video rounded-lg bg-gray-200 animate-pulse" />
              ))}
            </div>
          ) : hostifyPhotos && hostifyPhotos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {hostifyPhotos.map((photo, idx) => (
                <div key={photo.id} onClick={() => setPhotoIndex(idx)}
                  className="aspect-video rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:ring-2 hover:ring-sereno-dark/30 transition-all">
                  <img src={photo.url} alt={photo.caption || ''} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Image size={36} className="mx-auto text-sereno-dark/20 mb-2" />
              <p className="text-sm text-sereno-dark/40">{property.hostifyId ? 'Sin fotos disponibles en Hostify' : 'Sin fotos (sin ID de Hostify)'}</p>
            </div>
          )}
        </div>
      )}

      {/* === TAB: DISTRIBUCIÓN === */}
      {activeTab === 'distribucion' && (
        <div className="card">
          <div className="flex items-center gap-2 mb-5">
            <Home size={16} className="text-sereno-dark/50" />
            <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Distribución</h3>
          </div>
          {loadingRooms ? (
            <div className="space-y-5">
              {[1, 2].map(i => (
                <div key={i} className="pl-4 border-l-2 border-gray-200 space-y-2">
                  <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
                  <div className="h-3 w-48 bg-gray-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : !roomDistribution ? (
            <div className="flex flex-col items-center py-12 text-sereno-dark/30">
              <Home size={36} className="mb-2 opacity-30" />
              <p className="text-sm">Sin distribución configurada en lavandería</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {roomDistribution.rooms.map(room => {
                const style = ROOM_TYPE_STYLE[room.roomType || ''] || DEFAULT_ROOM_STYLE;
                return (
                  <div key={room.roomId} className={`pl-4 border-l-[3px] ${style.border} py-4 first:pt-0 last:pb-0`}>
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                      <span className="font-semibold text-sereno-dark">{room.name || `Habitación ${room.roomId}`}</span>
                      {room.personCapacity > 0 && <span className={`text-xs ${style.text} shrink-0`}>{room.personCapacity} pers.</span>}
                    </div>
                    {room.beds.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {room.beds.map((bed, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-sereno-dark/80">
                            <Bed size={13} className="text-sereno-dark/30 shrink-0" />
                            <span className="font-medium">{bed.tipo === 'sofaCama' ? 'Sofá cama' : BED_SIZE_LABEL[bed.tamano] || `${bed.tamano}cm`}</span>
                            <span className="text-sereno-dark/35 text-xs">{bed.tamano}cm</span>
                            {bed.almohadas && (<><span className="text-sereno-dark/20">·</span><span className="text-xs text-sereno-dark/50">{bed.almohadas.cantidad} alm. {bed.almohadas.tamano}cm</span></>)}
                          </div>
                        ))}
                      </div>
                    )}
                    {room.items.length > 0 && (
                      <p className="text-xs text-sereno-dark/50 leading-5">
                        {room.items.map((item, i) => (
                          <span key={i}>
                            {i > 0 && <span className="mx-1.5 text-sereno-dark/20">·</span>}
                            <span className="text-sereno-dark/60">{ROOM_ITEM_LABELS[item.tipo] || item.tipo}</span>
                            {item.tamano ? <span className="text-sereno-dark/35"> {item.tamano}cm</span> : null}
                            {item.cantidad > 1 ? <span className="font-semibold text-sereno-dark/50"> ×{item.cantidad}</span> : null}
                          </span>
                        ))}
                      </p>
                    )}
                  </div>
                );
              })}
              {roomDistribution.notes && <p className="pt-4 text-xs text-sereno-dark/50 italic">{roomDistribution.notes}</p>}
              {roomDistribution.updatedAt && (
                <p className="text-xs text-sereno-dark/25 text-right pt-3">
                  Actualizado {new Date(roomDistribution.updatedAt).toLocaleDateString('es-ES')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* === TAB: DOCUMENTOS === */}
      {activeTab === 'documentos' && (
        <div className="card space-y-4">
          <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Documentos</h3>
          <PropertyDocumentSection title="Licencia turística" propertyId={id!} ownerId={property.ownerId} category="licencia-turistica" documents={propertyDocs} onRefresh={refreshDocs} />
          <PropertyDocumentSection title="Registro de la Propiedad (NRA)" propertyId={id!} ownerId={property.ownerId} category="registro-propiedad" documents={propertyDocs} onRefresh={refreshDocs} />
        </div>
      )}

      {/* === TAB: ACTIVIDAD === */}
      {activeTab === 'actividad' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Actividad</h3>
            <button onClick={() => setShowAddActivity(true)} className="btn-secondary text-sm flex items-center gap-1">
              <Plus size={14} /> Añadir
            </button>
          </div>
          <ActivityTimeline activities={activities || []} />
        </div>
      )}

      <Modal isOpen={showAddActivity} onClose={() => setShowAddActivity(false)} title="Nueva actividad">
        <SimpleActivityForm onSubmit={handleAddActivity} onCancel={() => setShowAddActivity(false)} />
      </Modal>

      {/* Photo Lightbox */}
      {photoIndex !== null && hostifyPhotos && hostifyPhotos[photoIndex] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={() => setPhotoIndex(null)}>
          <button onClick={() => setPhotoIndex(null)} className="absolute top-4 right-4 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors z-10">
            <X size={24} />
          </button>
          {hostifyPhotos.length > 1 && (
            <>
              <button onClick={e => { e.stopPropagation(); setPhotoIndex((photoIndex - 1 + hostifyPhotos.length) % hostifyPhotos.length); }} className="absolute left-4 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors z-10">
                <ChevronLeft size={28} />
              </button>
              <button onClick={e => { e.stopPropagation(); setPhotoIndex((photoIndex + 1) % hostifyPhotos.length); }} className="absolute right-4 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors z-10">
                <ChevronRight size={28} />
              </button>
            </>
          )}
          <div className="max-w-5xl max-h-[90vh] mx-4" onClick={e => e.stopPropagation()}>
            <img src={hostifyPhotos[photoIndex].url} alt={hostifyPhotos[photoIndex].caption || ''} className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            {hostifyPhotos[photoIndex].caption && <p className="text-white/70 text-sm text-center mt-2">{hostifyPhotos[photoIndex].caption}</p>}
            <p className="text-white/40 text-xs text-center mt-1">{photoIndex + 1} / {hostifyPhotos.length}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function SimpleActivityForm({ onSubmit, onCancel }: { onSubmit: (data: any) => Promise<void>; onCancel: () => void }) {
  const [form, setForm] = useState({ activityType: 'note', title: '', description: '' });
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try { await onSubmit(form); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">Tipo</label>
        <select className="input" value={form.activityType} onChange={e => setForm(f => ({ ...f, activityType: e.target.value }))}>
          <option value="note">Nota</option><option value="call">Llamada</option><option value="email">Email</option>
          <option value="visit">Visita</option><option value="whatsapp">WhatsApp</option><option value="meeting">Reunión</option>
        </select>
      </div>
      <div><label className="label">Título *</label><input className="input" required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
      <div><label className="label">Descripción</label><textarea className="input" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Guardando...' : 'Guardar'}</button>
      </div>
    </form>
  );
}
